import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CssBaseline,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import {
  DashboardOutlined as DashboardIcon,
  LogoutOutlined as LogoutIcon,
  Inventory2Outlined as Inventory2Icon,
  CategoryOutlined as CategoryIcon,
  StorefrontOutlined as StorefrontIcon,
  GroupOutlined as PeopleIcon,
  ShoppingCartOutlined as ShoppingCartIcon,
  BrandingWatermarkOutlined as BrandingWatermarkIcon,
  ManageAccountsOutlined as ManageAccountsIcon,
  ExpandLess,
  ExpandMore,
  FactCheckOutlined as ListAltIcon,
  ShoppingBagOutlined as ShoppingBagIcon,
  CheckCircleOutlined as ApprovalIcon,
  FactoryOutlined as FactoryOutlinedIcon,
  WidgetsOutlined,
  FactoryOutlined,
  Money,
  SummarizeOutlined as KitchenReportIcon,
} from '@mui/icons-material';

import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import WifiIcon from '@mui/icons-material/WifiOutlined';
import { Link, useLocation } from 'react-router-dom';
import Header from './Header';
import logo from '../assets/logo.svg';
import Color from './Color';
import { getSidebarCounts } from '../service/dashboardService';
import { Badge } from '@mui/material';

const fullDrawerWidth = 240;
const collapsedDrawerWidth = 72;

const formatCount = (count) => {
  return count > 99 ? '99+' : count;
};

const Sidebar = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [openSubMenus, setOpenSubMenus] = useState({});

  const location = useLocation();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [companyLogo, setCompanyLogo] = useState(null);
  const [counts, setCounts] = useState({
    sweet_orders_count: 0,
    cake_orders_count: 0,
    sweet_factory_count: 0,
    cake_factory_count: 0,
    payments_count: 0,
    pending_approval: 0,
    pending_payment_approvals: 0,
  });

  const fetchCounts = async () => {
    try {
      const response = await getSidebarCounts();
      if (response.data.status === 'success') {
        setCounts(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching sidebar counts:', err);
    }
  };

  useEffect(() => {
    fetchCounts();
    // Optional: Refresh counts every 5 minutes
    const interval = setInterval(fetchCounts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isTablet) setDesktopOpen(false);
    else if (isDesktop) setDesktopOpen(true);
  }, [isTablet, isDesktop]);

  useEffect(() => {
    const settings = JSON.parse(localStorage.getItem('settings')) || {};
    if (settings.company_logo) {
      const logoURL = settings.company_logo.startsWith('http')
        ? settings.company_logo
        : `${process.env.REACT_APP_API_BASE_URL || ''}${settings.company_logo}`;
      setCompanyLogo(logoURL);
    }
  }, []);

  // Listen for settings updates
  useEffect(() => {
    const handleSettingsUpdate = (event) => {
      const updatedSettings = event.detail;
      if (updatedSettings.company_logo) {
        const logoURL = updatedSettings.company_logo.startsWith('http')
          ? updatedSettings.company_logo
          : `${process.env.REACT_APP_API_BASE_URL || ''}${updatedSettings.company_logo}`;
        setCompanyLogo(logoURL);
      }
    };

    window.addEventListener('settingsUpdated', handleSettingsUpdate);

    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsUpdate);
    };
  }, []);

  // 🔥 NEW: Listen for logo preview (before save)
  useEffect(() => {
    const handleLogoPreview = (event) => {
      setCompanyLogo(event.detail); // This is the object URL from file upload
    };

    window.addEventListener("logoPreviewUpdated", handleLogoPreview);

    return () => {
      window.removeEventListener("logoPreviewUpdated", handleLogoPreview);
    };
  }, []);

  // Listen for sidebar count updates via WebSocket
  useEffect(() => {
    const handleSidebarRefresh = (event) => {
      console.log("Refreshing sidebar counts from WebSocket...");
      fetchCounts();
    };

    window.addEventListener("sidebarRefresh", handleSidebarRefresh);

    return () => {
      window.removeEventListener("sidebarRefresh", handleSidebarRefresh);
    };
  }, []);


  const userRole = parseInt(localStorage.getItem('role')) || 0;

  const mainMenuItems = useMemo(() => [
    { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { text: 'Branch', path: '/branches', icon: <StorefrontIcon />, roles: [1] },
    { text: 'Brand', path: '/brands', icon: <BrandingWatermarkIcon />, roles: [1] },
    { text: 'Categories', path: '/categories', icon: <CategoryIcon />, roles: [1] },
    { text: 'Sub Categories', path: '/sub-categories', icon: <WidgetsOutlined />, roles: [1] },
    { text: 'Products', path: '/products', icon: <Inventory2Icon />, roles: [1] },
    { text: 'Customer', path: '/customer', icon: <PeopleIcon />, roles: [1] },
    {
      text: 'Orders',
      path: '/orders',
      icon: <ShoppingCartIcon />,
      count: counts.sweet_orders_count,
    },
    { text: 'Reports', path: '/reports', icon: <ListAltIcon /> },
    { text: 'Payments', path: '/payments', icon: <Money />, roles: [1, 2], count: counts.payments_count },
    { text: 'User', path: '/users', icon: <ManageAccountsIcon />, roles: [1] },
    { text: 'SalesMan', path: '/sales-man-list', icon: <PeopleIcon />, roles: [1] },
    {
      text: 'Approvals',
      path: '/approval',
      icon: <ApprovalIcon />,
      roles: [1],
      count: (Number(counts.pending_approval) || 0) + (Number(counts.pending_payment_approvals) || 0),
    },
    { text: 'Sweet Factory', path: '/sweet-factory', icon: <FactoryOutlined />, roles: [1, 3], count: counts.sweet_factory_count },
    { text: 'Cake Factory', path: '/cake-factory', icon: <FactoryOutlinedIcon />, roles: [1, 3], count: counts.cake_factory_count },
    { text: 'Kitchen Reports', path: '/kitchen-reports', icon: <KitchenReportIcon />, roles: [1, 3] },
    // { text: 'WS Monitor', path: '/ws-monitor', icon: <WifiIcon />, roles: [1] },
  ], [counts]);

  useEffect(() => {
    const initialSubMenus = {};
    mainMenuItems.forEach((item) => {
      if (item.children) {
        const isChildActive = item.children.some((child) =>
          location.pathname.startsWith(child.path)
        );
        initialSubMenus[item.text] = isChildActive;
      }
    });
    setOpenSubMenus(initialSubMenus);
  }, [location.pathname, mainMenuItems]);

  const getVisibleMenuItems = () => {
    if (userRole === 3) {
      return [
        // {
        //   text: 'Dashboard',
        //   path: '/Dashboard',
        //   icon: <DashboardIcon />,
        //   roles: [1, 3],
        // },
        {
          text: 'Sweet Factory',
          path: '/sweet-factory',
          icon: <FactoryOutlined />,
          roles: [1, 3],
          count: counts.sweet_factory_count,
        },
        {
          text: 'Cake Factory',
          path: '/cake-factory',
          icon: <FactoryOutlinedIcon />,
          roles: [1, 3],
          count: counts.cake_factory_count,
        },
        {
          text: 'Kitchen Reports',
          path: '/kitchen-reports',
          icon: <KitchenReportIcon />,
          roles: [1, 3],
        },
      ];
    }
    return mainMenuItems;
  };

  const currentDrawerWidth = desktopOpen ? fullDrawerWidth : collapsedDrawerWidth;
  const handleMobileToggle = () => setMobileOpen(!mobileOpen);
  const handleDesktopToggle = () => setDesktopOpen(!desktopOpen);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const toggleSubMenu = (key, open) => {
    const menuItem = mainMenuItems.find((item) => item.text === key);
    const isChildActive = menuItem?.children?.some((child) =>
      location.pathname.startsWith(child.path)
    );

    if (!open && isChildActive) return;

    setOpenSubMenus((prev) => ({ ...prev, [key]: open }));
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Box
        sx={{
          height: 70,
          display: 'flex',
          alignItems: 'center',
          justifyContent: desktopOpen ? 'flex-start' : 'center',
          px: desktopOpen ? 3 : 0,
          transition: 'all 0.3s ease',
          marginTop: mobileOpen ? 8 : 0,
        }}
      >
        {desktopOpen ? (
          <Box
            component='img'
            src={companyLogo || logo}
            alt='Logo'
            sx={{ height: 64, pl: '46px', objectFit: 'contain', mt: 0, mb: 0 }}
          />
        ) : (
          <Tooltip title='SVM BAKERY' placement='right'>
            <Box component='img' src={companyLogo || logo} alt='Logo' sx={{ height: 30, objectFit: 'contain' }} />
          </Tooltip>
        )}
      </Box>
      <Divider />

      {/* Menu Items */}
      <List sx={{ mt: 1, px: 1.5 }}>
        {getVisibleMenuItems().map((item) => {
          if (item.roles && !item.roles.includes(userRole)) return null;
          const isActive = item.path
            ? location.pathname.startsWith(item.path)
            : item.children?.some((child) => location.pathname.startsWith(child.path));
          const isOpen = openSubMenus[item.text] || false;

          if (item.children) {
            const parentProps = {
              sx: {
                justifyContent: desktopOpen || mobileOpen ? 'initial' : 'center',
                px: 2,
                py: 1,
                mb: 0.4,
                borderRadius: '12px',
                backgroundColor: isActive ? 'rgba(124, 11, 204, 0.08)' : 'transparent',
                transition: 'all 0.3s ease',
                position: 'relative',
                '&:hover': {
                  backgroundColor: isActive ? 'rgba(124, 11, 204, 0.12)' : 'rgba(0,0,0,0.03)',
                  transform: 'translateX(4px)',
                },
                ...(isActive && (desktopOpen || mobileOpen) && {
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -8,
                    top: '20%',
                    height: '60%',
                    width: '4px',
                    backgroundColor: Color.primary.main,
                    borderRadius: '0 4px 4px 0',
                  }
                })
              },
            };

            const parentItem = (
              <ListItem
                button
                {...(!item.children || item.path ? { component: Link, to: item.path } : {})}
                onClick={() => {
                  if (!item.hover) toggleSubMenu(item.text, !isOpen);
                  if (mobileOpen && item.path) setMobileOpen(false);
                }}
                {...parentProps}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: desktopOpen || mobileOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: isActive ? Color.primary.main : '#64748b',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(desktopOpen || mobileOpen) && (
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{item.text}</span>
                        {item.count > 0 && (
                          <Box
                            sx={{
                              ml: item.children ? 0.5 : 1,
                              minWidth: 20,
                              width: 'auto',
                              height: 20,
                              borderRadius: '10px',
                              px: 0.5,
                              backgroundColor: Color.primary.main,
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              fontFamily: '"Poppins", sans-serif',
                              flexShrink: 0
                            }}
                          >
                            {formatCount(item.count)}
                          </Box>
                        )}
                      </Box>
                    }
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? Color.primary.main : '#334155',
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  />
                )}
                {(desktopOpen || mobileOpen) && item.children && (
                  <Box
                    sx={{
                      ml: 1,
                      display: 'flex',
                      alignItems: 'center',
                      color: isActive ? Color.primary.main : '#94a3b8',
                      transition: 'transform 0.3s ease',
                      transform: isOpen ? 'rotate(0deg)' : 'rotate(0deg)',
                    }}
                  >
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                )}
              </ListItem>
            );

            return (
              <Box
                key={item.text}
                onMouseEnter={() => item.hover && toggleSubMenu(item.text, true)}
                onMouseLeave={() => item.hover && toggleSubMenu(item.text, false)}
                sx={{ width: '100%' }}
              >
                {parentItem}

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component='div' disablePadding sx={{ width: '100%', pl: 2 }}>
                    {item.children.map((child) => {
                      const childActive = location.pathname.startsWith(child.path);
                      return (
                        <Tooltip
                          key={child.text}
                          title={desktopOpen || mobileOpen ? '' : child.text}
                          placement='right'
                        >
                          <ListItem
                            button
                            component={Link}
                            to={child.path}
                            onClick={() => {
                              localStorage.removeItem('cancelReferenceOrderNumber');
                              setMobileOpen(false);
                            }}
                            sx={{
                              justifyContent: desktopOpen || mobileOpen ? 'initial' : 'center',
                              px: 2,
                              py: 0.7,
                              mb: 0.4,
                              borderRadius: '10px',
                              backgroundColor: childActive ? 'rgba(124, 11, 204, 0.05)' : 'transparent',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.03)',
                                pl: desktopOpen || mobileOpen ? 2.5 : 2
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: desktopOpen || mobileOpen ? 2 : 'auto',
                                justifyContent: 'center',
                                color: childActive ? Color.primary.main : '#94a3b8',
                                fontSize: '18px',
                              }}
                            >
                              {React.cloneElement(child.icon, { sx: { fontSize: 20 } })}
                            </ListItemIcon>
                            {(desktopOpen || mobileOpen) && (
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span>{child.text}</span>
                                    {child.count > 0 && (
                                      <Box
                                        sx={{
                                          ml: 1,
                                          minWidth: 18,
                                          width: 'auto',
                                          height: 18,
                                          borderRadius: '9px',
                                          px: 0.5,
                                          backgroundColor: Color.primary.main,
                                          color: '#fff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '0.65rem',
                                          fontWeight: 600,
                                          fontFamily: '"Poppins", sans-serif',
                                          flexShrink: 0
                                        }}
                                      >
                                        {formatCount(child.count)}
                                      </Box>
                                    )}
                                  </Box>
                                }
                                primaryTypographyProps={{
                                  fontSize: '0.8rem',
                                  fontWeight: childActive ? 600 : 400,
                                  color: childActive ? Color.primary.main : '#64748b',
                                  fontFamily: '"Poppins", sans-serif',
                                }}
                              />
                            )}
                          </ListItem>
                        </Tooltip>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }

          // Normal item
          return (
            <Tooltip key={item.text} title={desktopOpen || mobileOpen ? '' : item.text} placement='right'>
              <ListItem
                button
                component={Link}
                to={item.path}
                onClick={() => {
                  localStorage.removeItem('cancelReferenceOrderNumber');
                  setMobileOpen(false);
                }}
                sx={{
                  justifyContent: desktopOpen || mobileOpen ? 'initial' : 'center',
                  px: 2,
                  py: 1,
                  mb: 0.6,
                  borderRadius: '12px',
                  backgroundColor: isActive ? 'rgba(124, 11, 204, 0.08)' : 'transparent',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(124, 11, 204, 0.12)' : 'rgba(0,0,0,0.03)',
                    transform: 'translateX(4px)',
                  },
                  ...(isActive && (desktopOpen || mobileOpen) && {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: -8,
                      top: '20%',
                      height: '60%',
                      width: '4px',
                      backgroundColor: Color.primary.main,
                      borderRadius: '0 4px 4px 0',
                    }
                  })
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: desktopOpen || mobileOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: isActive ? Color.primary.main : '#64748b',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(desktopOpen || mobileOpen) && (
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{item.text}</span>
                        {item.count > 0 && (
                          <Box
                            sx={{
                              ml: 1,
                              minWidth: 20,
                              width: 'auto',
                              height: 20,
                              borderRadius: '10px',
                              px: 0.5,
                              backgroundColor: Color.primary.main,
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              fontFamily: '"Poppins", sans-serif',
                              flexShrink: 0
                            }}
                          >
                            {formatCount(item.count)}
                          </Box>
                        )}
                      </Box>
                    }
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? Color.primary.main : '#334155',
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  />
                )}
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {/* Logout - Hidden in mobile view */}
      {!isMobile && (
        <List>
          <Tooltip title={desktopOpen ? '' : 'Logout'} placement='right'>
            <ListItem
              button
              onClick={handleLogout}
              sx={{
                justifyContent: desktopOpen ? 'initial' : 'center',
                px: 1,
                py: 0.5,
                mx: 1,
                borderRadius: 2,
                background: '#D51721',
                minHeight: 32,
                '&:hover': { backgroundColor: '#D51721' },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: desktopOpen ? 2 : 'auto',
                  justifyContent: 'center',
                  color: '#ffffff',
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {desktopOpen && (
                <ListItemText
                  primary='Logout'
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#fff',
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        </List>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100vw', overflowX: 'hidden' }}>
      <CssBaseline />
      <Header
        currentDrawerWidth={currentDrawerWidth}
        handleMobileToggle={handleMobileToggle}
        handleDesktopToggle={handleDesktopToggle}
        desktopOpen={desktopOpen}
      />

      <Box component='nav' sx={{ width: { md: currentDrawerWidth, xs: 0 }, flexShrink: 0 }}>
        {/* Mobile Drawer */}
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleMobileToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: fullDrawerWidth,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              transition: 'none !important',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant='permanent'
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: currentDrawerWidth,
              backgroundColor: '#ffffff',
              boxShadow: 'none',
              border: 'none',
              transition: 'width 0.3s ease',
              boxSizing: 'border-box',
              overflowX: 'hidden',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component='main'
        sx={{
          flexGrow: 1,
          mt: '50px',
          height: 'calc(100vh - 50px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          transition: (theme) =>
            theme.transitions.create(['width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
          backgroundColor: '#F0F1F7',
          p: { xs: 2, sm: 4, md: 4, lg: 4 },
          boxSizing: 'border-box',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Sidebar;